// ============================================================================
// Time & Labor — the data layer. useEffect loaders over shared/supabase
// (no TanStack Query in this app), RPCs for every write to the punch log.
//
// Who sees what is decided by RLS, not here: punch data reads for
// hr / finance / executive / superuser; reference data for any app employee.
// Direct INSERT/UPDATE on punch_events and shift_segments is revoked — the
// ONLY write paths are rpc_time_punch and rpc_time_correct_segment.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase, getSupabaseToken, SUPABASE_ANON_KEY } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
import { commitWrite } from '../../../shared/save-path';
import type {
  AssignmentRow,
  CoachingRow,
  Contractor1099Payload,
  ExceptionRow,
  ExportHistoryRow,
  FacilityOption,
  LocationState,
  PayrollSummaryPayload,
  PaysheetPayload,
  PunchHistoryRow,
  PunchResult,
  ReportLinkRow,
  ReportLinkScope,
  RemovedShiftRow,
  ResidualRow,
  SegmentRow,
  ServiceLineRow,
  WorkerPickerRow,
  WorkerRow,
} from '../types';
import type {
  BuddyPunchWorker,
  InOutException,
  InOutInput,
  InOutPunch,
  InOutSegment,
} from '../inout-activity';

// ---------------------------------------------------------------------------
// Loaders
// ---------------------------------------------------------------------------

export function useExceptionQueue() {
  const [rows, setRows] = useState<ExceptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('v_time_exceptions_open')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (err) throw err;
      setRows((data ?? []) as unknown as ExceptionRow[]);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the exception queue.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, reload: load };
}

export function useSegments(filter: { workerId?: string; facilityId?: string }) {
  const [rows, setRows] = useState<SegmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // ⛔ A REMOVED SHIFT LEAVES THE PUNCH CARD (Ben, 2026-09-22): "that
      // record should be tied to the PERSON in a deleted/hidden punch log,
      // NOT show up in the punch card." It is excluded HERE, on the list,
      // and never from the view — v_time_segments_detail stays the complete
      // record of every shift, and useRemovedSegments reads the other half
      // of it. TCSD-0163: a picker excludes from the LIST, never the fetch.
      let q = supabase
        .from('v_time_segments_detail')
        .select('*')
        .neq('segment_status', 'void')
        .order('started_at', { ascending: false })
        .limit(300);
      if (filter.workerId) q = q.eq('worker_id', filter.workerId);
      if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
      const { data, error: err } = await q;
      if (err) throw err;
      setRows((data ?? []) as unknown as SegmentRow[]);
    } catch (e) {
      setError(errorMessage(e, 'Could not load timecards.'));
    } finally {
      setLoading(false);
    }
  }, [filter.workerId, filter.facilityId]);

  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, reload: load };
}

/**
 * Every shift that was REMOVED, with who removed it, when and why.
 *
 * ⛔ Ben, 2026-09-22: the record of a removal is kept and it is tied to the
 * PERSON — it is simply not in the punch card. Read with a worker filter it
 * is that person's removed list, behind one line at the bottom of their card;
 * read with none it is the whole module's Removed tab. Ben chose both, and
 * they are the same read with and without a filter, not two builds.
 *
 * The view is security_invoker, so an account manager sees removals at their
 * own buildings and nowhere else, exactly as they see the shifts themselves.
 */
export function useRemovedSegments(filter: { workerId?: string; facilityId?: string }) {
  const [rows, setRows] = useState<RemovedShiftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let q = supabase
        .from('v_time_segments_removed')
        .select('*')
        .order('removed_at', { ascending: false, nullsFirst: false })
        .limit(300);
      if (filter.workerId) q = q.eq('worker_id', filter.workerId);
      if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
      const { data, error: err } = await q;
      if (err) throw err;
      setRows((data ?? []) as unknown as RemovedShiftRow[]);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the removed shifts.'));
    } finally {
      setLoading(false);
    }
  }, [filter.workerId, filter.facilityId]);

  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, reload: load };
}

/** Auto-closes the worker has never been PROMPTED about (no punch since the
 *  exception was raised) — the console's follow-up list, and the pre-cutoff
 *  sweep before payroll (Decision 1: worst case is one untouched pay cycle). */
export function useAutocloseResidual() {
  const [rows, setRows] = useState<ResidualRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('v_time_autoclose_residual')
        .select('*')
        .limit(300);
      if (err) throw err;
      setRows((data ?? []) as unknown as ResidualRow[]);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the follow-up list.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, reload: load };
}

/** The escalation ceiling as a VIEW (never a stored counter): 3+ open
 *  auto-closes, or 2+ inside the open pay period → a coaching conversation. */
export function useAutocloseCoaching() {
  const [rows, setRows] = useState<CoachingRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('v_time_autoclose_coaching')
        .select('*');
      if (err) throw err;
      setRows((data ?? []) as unknown as CoachingRow[]);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the coaching list.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { rows, error, reload: load };
}

export function useReportLinks() {
  const [links, setLinks] = useState<ReportLinkRow[]>([]);
  const [facilitiesByLink, setFacilitiesByLink] = useState<Map<string, string[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, lf] = await Promise.all([
        supabase.from('hours_report_links').select('*').order('created_at', { ascending: false }),
        supabase.from('hours_report_link_facilities').select('link_id, facility_id'),
      ]);
      if (l.error) throw l.error;
      if (lf.error) throw lf.error;
      const m = new Map<string, string[]>();
      for (const r of (lf.data ?? []) as Array<{ link_id: string; facility_id: string }>) {
        const arr = m.get(r.link_id) ?? [];
        arr.push(r.facility_id);
        m.set(r.link_id, arr);
      }
      setLinks((l.data ?? []) as unknown as ReportLinkRow[]);
      setFacilitiesByLink(m);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load hours-report links.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { links, facilitiesByLink, loading, error, reload: load };
}

export interface ProviderOption {
  provider_id: string;
  display: string;
  /** At least one worker is recorded as employed by this sub, so a sub link has hours to show. */
  hasCrew: boolean;
}

export interface EmployerOption {
  provider_id: string;
  /** The company, with its owner: "Shiny Floors Cleaning Service LLC (Rodrigo Ibarra)". */
  display: string;
}

/**
 * "Works for" — the subcontractor companies on file (worker_class 'subcontractor', Active).
 * A worker who works for one is Subcontractor's crew and never on TCS payroll
 * (workers_crew_has_employer; Ben, 28 Oct, option 2 on the one-cleaner-list item).
 */
export function useEmployerOptions() {
  const [rows, setRows] = useState<EmployerOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void (async () => {
      const { data, error: err } = await supabase
        .from('service_providers')
        .select('provider_id, legal_business_name, partner_name, owner_name')
        .eq('worker_class', 'subcontractor')
        .eq('provider_status', 'Active');
      // A failed read must not look like "no subcontractors on file": say so, offer none.
      if (err) { setError(errorMessage(err, 'Could not load the subcontractor companies.')); return; }
      const opts = ((data ?? []) as Array<{
        provider_id: string; legal_business_name: string | null; partner_name: string | null; owner_name: string | null;
      }>).map((p) => {
        const company = p.legal_business_name?.trim() || p.partner_name || p.provider_id;
        const owner = p.owner_name?.trim();
        return { provider_id: p.provider_id, display: owner && owner !== company ? `${company} (${owner})` : company };
      });
      opts.sort((a, b) => a.display.localeCompare(b.display));
      setRows(opts);
    })();
  }, []);
  return { rows, error };
}

/** Subs (service providers) for the provider-scoped report links. */
export function useProviderOptions() {
  const [rows, setRows] = useState<ProviderOption[]>([]);
  useEffect(() => {
    void (async () => {
      const [{ data }, { data: crew }] = await Promise.all([
        supabase
          .from('service_providers')
          .select('provider_id, owner_name, partner_name')
          .order('provider_id'),
        // A sub link finds the crew through each worker's employing sub — the same link the
        // public page reads — so only a sub with a worker on record can be given one.
        supabase.from('workers').select('employer_provider_id').not('employer_provider_id', 'is', null),
      ]);
      const employers = new Set(
        ((crew ?? []) as Array<{ employer_provider_id: string | null }>).map((w) => w.employer_provider_id),
      );
      setRows(((data ?? []) as Array<{ provider_id: string; owner_name: string | null; partner_name: string | null }>)
        .map((p) => ({
          provider_id: p.provider_id,
          display: p.owner_name ?? p.partner_name ?? p.provider_id,
          hasCrew: employers.has(p.provider_id),
        })));
    })();
  }, []);
  return rows;
}

export function useWorkerPicker() {
  // PICKERS EXCLUDE (TCSD-0129): v_time_worker_picker is active workers only.
  const [rows, setRows] = useState<WorkerPickerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('v_time_worker_picker')
        .select('*')
        .order('last_name');
      if (err) throw err;
      setRows((data ?? []) as unknown as WorkerPickerRow[]);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load workers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, reload: load };
}

export function useWorkersAdmin() {
  const [workers, setWorkers] = useState<WorkerRow[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, a] = await Promise.all([
        supabase.from('workers').select(
          'worker_id, first_name, last_name, preferred_name, phone_mobile, email, preferred_language, ' +
          'worker_status, employment_class, employer_provider_id, employee_id, linked_provider_id, work_state, notes'
        ).order('last_name'),
        supabase.from('worker_assignments').select(
          'assignment_id, worker_id, facility_id, market_region, service_line_code, shift_code, provider_id, ' +
          'scheduled_hours_per_week, authorized_hours_per_week, scheduled_hours_per_shift, unpaid_break_minutes, ' +
          'effective_start, effective_end'
        ).order('created_at', { ascending: false }),
      ]);
      if (w.error) throw w.error;
      if (a.error) throw a.error;
      setWorkers((w.data ?? []) as unknown as WorkerRow[]);
      setAssignments((a.data ?? []) as unknown as AssignmentRow[]);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load workers.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { workers, assignments, loading, error, reload: load };
}

export function useServiceLines() {
  const [rows, setRows] = useState<ServiceLineRow[]>([]);
  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('service_lines')
        .select('service_line_code, label, sort_order')
        .eq('is_active', true)
        .order('sort_order');
      setRows((data ?? []) as unknown as ServiceLineRow[]);
    })();
  }, []);
  return rows;
}

/** Active facilities with the owner in parentheses (the standing naming rule),
 *  resolved once here for the whole module — never a COALESCE at a call site. */
export function useFacilityOptions() {
  const [rows, setRows] = useState<FacilityOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [fac, asn, prov] = await Promise.all([
          supabase.from('facilities').select('facility_id, facility_name, account_manager_eid')
            .eq('service_state', 'Active').order('facility_name'),
          supabase.from('service_assignments').select('facility_id, provider_id, created_at')
            .eq('assignment_status', 'Active'),
          supabase.from('service_providers').select('provider_id, owner_name, partner_name'),
        ]);
        const ownerByProvider = new Map<string, string>(
          (prov.data ?? []).map((p: { provider_id: string; owner_name: string | null; partner_name: string | null }) =>
            [p.provider_id, p.owner_name ?? p.partner_name ?? '']),
        );
        const ownerByFacility = new Map<string, string>();
        const sorted = [...(asn.data ?? [])].sort(
          (a: { created_at: string }, b: { created_at: string }) => a.created_at.localeCompare(b.created_at));
        for (const a of sorted as Array<{ facility_id: string; provider_id: string | null }>) {
          if (!a.provider_id || ownerByFacility.has(a.facility_id)) continue;
          const owner = ownerByProvider.get(a.provider_id);
          if (owner) ownerByFacility.set(a.facility_id, owner);
        }
        setRows(((fac.data ?? []) as Array<{ facility_id: string; facility_name: string; account_manager_eid: string | null }>).map((f) => {
          const owner = ownerByFacility.get(f.facility_id);
          return {
            facility_id: f.facility_id,
            display: owner ? `${f.facility_name} (${owner})` : f.facility_name,
            name: f.facility_name,
            account_manager_eid: f.account_manager_eid,
          };
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { rows, loading };
}

// ---------------------------------------------------------------------------
// Actions — thin RPC wrappers. Every one rethrows; callers show describeError.
// ---------------------------------------------------------------------------

export interface PunchInput {
  workerId: string;
  eventType: 'in' | 'out';
  captureMode: 'kiosk_pin' | 'mobile_web' | 'web_staff';
  facilityId?: string | null;
  marketRegion?: string | null;
  serviceLineCode?: string | null;
  shiftCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  gpsAccuracyM?: number | null;
  locationState?: LocationState;
  deviceFingerprint?: string | null;
  deviceTimezone?: string | null;
  kioskPin?: string | null;
}

export async function punch(input: PunchInput): Promise<PunchResult> {
  const { data, error } = await supabase.rpc('rpc_time_punch', {
    p_worker_id: input.workerId,
    p_event_type: input.eventType,
    p_capture_mode: input.captureMode,
    p_facility_id: input.facilityId ?? null,
    p_market_region: input.marketRegion ?? null,
    p_service_line_code: input.serviceLineCode ?? null,
    p_shift_code: input.shiftCode ?? null,
    p_latitude: input.latitude ?? null,
    p_longitude: input.longitude ?? null,
    p_gps_accuracy_m: input.gpsAccuracyM ?? null,
    p_location_state: input.locationState ?? null,
    p_photo_url: null,
    p_device_fingerprint: input.deviceFingerprint ?? null,
    p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
    p_device_timezone: input.deviceTimezone ?? null,
    p_kiosk_pin: input.kioskPin ?? null,
  });
  if (error) throw error;
  return data as unknown as PunchResult;
}

/**
 * Everything that ever happened to one night's punches, in order.
 *
 * ⛔ Ben, relaying HR, 2026-09-22: "There is no way to look at a punch to see
 * the edit history (when/how/where it was punched)." The trail was always
 * complete — a correction inserts a new punch carrying `supersedes_event_id`
 * and never touches the original — and there was simply no way to ask for it.
 *
 * Fetched on demand, per row, because a timecard with 300 shifts must not
 * fetch 300 chains nobody opened.
 */
export function usePunchHistory() {
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [rows, setRows] = useState<PunchHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(async (id: string | null) => {
    setSegmentId(id);
    setRows([]);
    setError(null);
    if (!id) return;
    setLoading(true);
    try {
      const { data, error: err } = await supabase.rpc('fn_time_punch_history', { p_segment_id: id });
      if (err) throw err;
      setRows((data ?? []) as unknown as PunchHistoryRow[]);
    } catch (e) {
      setError(errorMessage(e, 'Could not read this punch’s history.'));
    } finally {
      setLoading(false);
    }
  }, []);

  return { segmentId, rows, loading, error, open };
}

/**
 * The worker's clock for a building or a market that has not been saved yet —
 * the record-a-shift form needs it BEFORE there is a row to carry `site_tz`.
 *
 * ⛔ It asks the database's own rule (`fn_time_site_tz`: the building's market,
 * else the market, from `markets.timezone`) rather than working it out here, so
 * the form and every stored row can never disagree about which clock a
 * building is on (Ben, 2026-09-24, Decisions 1 and 4). Null while nothing is
 * chosen or the answer is still on its way — the time fields wait for it.
 */
export function useSiteTz(facilityId: string | null, marketRegion: string | null) {
  const [tz, setTz] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTz(null);
    setError(null);
    if (!facilityId && !marketRegion) return;
    void (async () => {
      const { data, error: err } = await supabase.rpc('fn_time_site_tz', {
        p_facility_id: facilityId ?? '',
        p_market_region: marketRegion ?? '',
      });
      if (cancelled) return;
      if (err || typeof data !== 'string' || data === '') {
        setError(errorMessage(err, 'Could not work out which clock that building is on.'));
        return;
      }
      setTz(data);
    })();
    return () => { cancelled = true; };
  }, [facilityId, marketRegion]);

  return { tz, error };
}

export async function correctSegment(
  segmentId: string,
  startedAt: string | null,
  endedAt: string | null,
  reason: string,
  voidSegment = false,
): Promise<void> {
  const { error } = await supabase.rpc('rpc_time_correct_segment', {
    p_segment_id: segmentId,
    p_started_at: startedAt,
    p_ended_at: endedAt,
    p_reason: reason,
    p_void: voidSegment,
  });
  if (error) throw error;
}

export async function reviewException(exceptionId: string, note: string, reviewerEid: string | null): Promise<void> {
  const { error } = await supabase
    .from('segment_exceptions')
    .update({
      status: 'reviewed',
      review_note: note,
      reviewed_by_eid: reviewerEid,
      reviewed_at: new Date().toISOString(),
    })
    .eq('exception_id', exceptionId);
  if (error) throw error;
}

export type AutocloseResolution = 'forgot_out' | 'worked_full' | 'not_sure' | 'defer';

/** The worker's answer to an auto-closed shift, given at the next punch.
 *  Asymmetric verification on purpose (Decision 1): forgot_out (LESS pay) is
 *  auto-accepted; worked_full (MORE pay) routes to HR before payroll. */
export async function resolveAutoclose(
  exceptionId: string,
  resolution: AutocloseResolution,
  punchOutAt?: string | null,
  note?: string | null,
): Promise<{ segment_id?: string; raw_minutes?: number; status?: string; dispute_id?: string; routed_to?: string; deferred_until?: string }> {
  const { data, error } = await supabase.rpc('rpc_time_resolve_autoclose', {
    p_exception_id: exceptionId,
    p_resolution: resolution,
    p_punch_out_at: punchOutAt ?? null,
    p_note: note ?? null,
  });
  if (error) throw error;
  return data as unknown as { segment_id?: string; raw_minutes?: number; status?: string; dispute_id?: string };
}

/** HR's confirmation of a worked-full claim (the expensive path needs a
 *  person). confirmFull=false sends it to the correction drawer instead. */
export async function staffResolveAutoclose(
  exceptionId: string,
  confirmFull: boolean,
  note?: string | null,
): Promise<{ segment_id?: string; status?: string }> {
  const { data, error } = await supabase.rpc('rpc_time_staff_resolve_autoclose', {
    p_exception_id: exceptionId,
    p_confirm_full: confirmFull,
    p_note: note ?? null,
  });
  if (error) throw error;
  return data as unknown as { segment_id?: string; status?: string };
}

export interface ManualSegmentInput {
  workerId: string;
  startedAt: string;   // ISO
  endedAt: string;     // ISO, must be past
  reason: string;      // mandatory — how did these times reach you?
  facilityId?: string | null;   // exactly one of facilityId / marketRegion
  marketRegion?: string | null;
  serviceLineCode?: string | null;
  shiftCode?: string | null;
}

/** HR's after-the-fact entry for call-ins ("kiosk down, phone broke, calling
 *  from the EVS director's phone"). Creates a CLOSED segment whose punches
 *  carry capture_mode=supervisor_manual and the reason on the record. */
export async function manualSegment(input: ManualSegmentInput): Promise<{ segment_id: string; raw_minutes: number }> {
  const { data, error } = await supabase.rpc('rpc_time_manual_segment', {
    p_worker_id: input.workerId,
    p_started_at: input.startedAt,
    p_ended_at: input.endedAt,
    p_reason: input.reason,
    p_facility_id: input.facilityId ?? null,
    p_market_region: input.marketRegion ?? null,
    p_service_line_code: input.serviceLineCode ?? null,
    p_shift_code: input.shiftCode ?? null,
  });
  if (error) throw error;
  return data as unknown as { segment_id: string; raw_minutes: number };
}

export async function createReportLink(
  scope: ReportLinkScope,
  label: string,
  facilityIds: string[] | null,
  providerId: string | null,
  days: number,
  /** A building link narrowed to one service line (Ben, 2026-10-23). Null = the whole building. */
  serviceLineCode: string | null = null,
): Promise<{ link_id: string; token: string; path: string; expires_at: string }> {
  const { data, error } = await supabase.rpc('rpc_time_create_report_link', {
    p_scope: scope,
    p_label: label,
    p_facility_ids: facilityIds,
    p_provider_id: providerId,
    p_days: days,
    p_service_line_code: serviceLineCode,
  });
  if (error) throw error;
  return data as unknown as { link_id: string; token: string; path: string; expires_at: string };
}

/** Revoking goes through its own database step (Ben, 2026-10-23): anyone who can
 *  make a building link can revoke one, without being able to change its
 *  buildings or its expiry. The row stays on the record, and the audit trail
 *  records who revoked it. */
export async function revokeReportLink(linkId: string): Promise<void> {
  const { error } = await supabase.rpc('rpc_time_revoke_report_link', { p_link_id: linkId });
  if (error) throw error;
}

/** Opens per link, from the view every hours page writes to (one row per link × building). */
export interface LinkOpensRow {
  link_id: string;
  facility_id: string | null;
  link_scope: string;
  link_is_live: boolean;
  last_opened_at: string | null;
  opens_30d: number;
  usage_state: string;
}

export function useLinkOpens() {
  const [rows, setRows] = useState<LinkOpensRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    const { data, error: e } = await supabase
      .from('v_time_client_hours_views')
      .select('link_id, facility_id, link_scope, link_is_live, last_opened_at, opens_30d, usage_state');
    if (e) { setError(errorMessage(e, 'Could not load who opened the links.')); return; }
    setRows((data ?? []) as unknown as LinkOpensRow[]);
    setError(null);
  }, []);
  useEffect(() => { void load(); }, [load]);
  return { rows, error, reload: load };
}

/** worker_id → the People directory card's group, so a worker's name can open it. */
export function useWorkerPersonGroups(): Map<string, string> {
  const [map, setMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    void (async () => {
      const { data, error } = await supabase
        .from('v_people_directory')
        .select('record_id, person_group')
        .eq('source', 'worker');
      if (error) return; // the names still show; they just do not link
      setMap(new Map(((data ?? []) as Array<{ record_id: string; person_group: string | null }>)
        .filter((r) => r.person_group)
        .map((r) => [r.record_id, r.person_group as string])));
    })();
  }, []);
  return map;
}

export async function setWorkerPin(workerId: string, pin: string): Promise<void> {
  const { error } = await supabase.rpc('rpc_time_set_worker_pin', {
    p_worker_id: workerId,
    p_pin: pin,
  });
  if (error) throw error;
}

export interface NewWorkerInput {
  firstName: string;
  lastName: string;
  preferredName?: string;
  phoneMobile?: string;
  email?: string;
  preferredLanguage: string;
  employmentClass: string;
  /** Works for: the subcontractor that employs and pays them. Omit for TCS. */
  employerProviderId?: string | null;
  workState?: string;
  createdByEid: string | null;
}

export async function createWorker(input: NewWorkerInput): Promise<string> {
  // Never mint a PK client-side; the BEFORE INSERT trigger assigns WRK-.
  const { data, error } = await supabase
    .from('workers')
    .insert({
      first_name: input.firstName,
      last_name: input.lastName,
      preferred_name: input.preferredName || null,
      phone_mobile: input.phoneMobile || null,
      email: input.email || null,
      preferred_language: input.preferredLanguage,
      employment_class: input.employmentClass,
      employer_provider_id: input.employerProviderId || null,
      work_state: input.workState || null,
      created_by_eid: input.createdByEid,
    })
    .select('worker_id')
    .single();
  if (error) throw error;
  return (data as { worker_id: string }).worker_id;
}

export interface NewAssignmentInput {
  workerId: string;
  facilityId?: string | null;   // exactly one of facilityId / marketRegion
  marketRegion?: string | null;
  serviceLineCode: string;
  shiftCode?: string | null;
  providerId?: string | null;
  authorizedHoursPerWeek?: number | null;
  /** Scheduled span per shift — NULL means unknown, and unknown never flags. */
  scheduledHoursPerShift?: number | null;
  unpaidBreakMinutes?: number | null;
  createdByEid: string | null;
}

export async function createAssignment(input: NewAssignmentInput): Promise<string> {
  const { data, error } = await supabase
    .from('worker_assignments')
    .insert({
      worker_id: input.workerId,
      facility_id: input.facilityId ?? null,
      market_region: input.marketRegion ?? null,
      service_line_code: input.serviceLineCode,
      shift_code: input.shiftCode ?? null,
      provider_id: input.providerId ?? null,
      authorized_hours_per_week: input.authorizedHoursPerWeek ?? null,
      scheduled_hours_per_shift: input.scheduledHoursPerShift ?? null,
      unpaid_break_minutes: input.unpaidBreakMinutes ?? 0,
      created_by_eid: input.createdByEid,
    })
    .select('assignment_id')
    .single();
  if (error) throw error;
  return (data as { assignment_id: string }).assignment_id;
}

// ---------------------------------------------------------------------------
// Package three — payroll generators. Every call computes server-side AND
// writes an immutable period_exports row (the audit record); regenerating the
// same scope+range mints the next version, superseding the prior one.
// ---------------------------------------------------------------------------

export async function generatePaysheets(providerId: string, weekEnding: string): Promise<PaysheetPayload> {
  const { data, error } = await supabase.rpc('rpc_time_paysheets', {
    p_provider_id: providerId,
    p_week_ending: weekEnding,
  });
  if (error) throw error;
  return data as unknown as PaysheetPayload;
}

export async function generatePayrollSummary(start: string, end: string): Promise<PayrollSummaryPayload> {
  const { data, error } = await supabase.rpc('rpc_time_payroll_summary', {
    p_start: start,
    p_end: end,
  });
  if (error) throw error;
  return data as unknown as PayrollSummaryPayload;
}

export async function generateContractor1099(start: string, end: string): Promise<Contractor1099Payload> {
  const { data, error } = await supabase.rpc('rpc_time_contractor_1099', {
    p_start: start,
    p_end: end,
  });
  if (error) throw error;
  return data as unknown as Contractor1099Payload;
}

// ---------------------------------------------------------------------------
// The pay run (view four).
//
// ⛔ OPENING IT RECORDS NOTHING. `runPayRun(start, end)` is a read; only
// `runPayRun(start, end, true)` — the Export button — writes the versioned
// ledger row, and it is the only call in this file that does.
// ---------------------------------------------------------------------------

export async function runPayRun(
  start: string, end: string, doExport = false,
): Promise<import('../types').PayRunPayload> {
  const { data, error } = await supabase.rpc('rpc_time_pay_run', {
    p_start: start,
    p_end: end,
    p_export: doExport,
  });
  if (error) throw error;
  return data as unknown as import('../types').PayRunPayload;
}

// ---------------------------------------------------------------------------
// The sub picker on /time/reports — the groups the search puts ABOVE the plain
// list of subs. ⛔ Not a second picker: SmartSelect renders these, and every
// other call site of it is untouched.
//
// Two things a person actually searches for on this screen: the name of a sub,
// and the name of somebody who works for one. Typing a person's name and
// getting nothing is what made the old <select> useless.
// ---------------------------------------------------------------------------
/**
 * HR sets which language we speak to a worker in, on the worker's own record.
 *
 * ⛔ The SAME column the clock page writes through to — there is one fact here
 * and one place it lives. ⛔ `.select()` or the write proves nothing: PostgREST
 * answers a PATCH with 204 whether it matched a row or matched none, so an RLS
 * refusal would be silent and HR would sit looking at their own click
 * (PLAYBOOK §15.1).
 */
export async function setWorkerLanguage(workerId: string, language: 'es' | 'en'): Promise<void> {
  await commitWrite({
    label: 'set the language we speak to this worker in',
    write: supabase.from('workers').update({ preferred_language: language })
      .eq('worker_id', workerId).select('worker_id'),
    touches: ['workers'],
  });
}

export interface SubPickerPerson {
  worker_id: string;
  name: string;
  /** Their employer. NULL is the REGISTER gap, not a data error — it is what
   *  Antonela confirms after October, and it is why their sheet would be
   *  empty. The picker says so rather than hiding them. */
  provider_id: string | null;
  provider_name: string | null;
}

export interface SubPickerWorked {
  provider_id: string;
  provider_name: string;
  people: number;
  buildings: number;
}

/**
 * ⛔ READS `error` AND SAYS WHICH LIST FAILED. A refused read here becomes an
 * empty picker, and an empty picker looks exactly like a company that employs
 * nobody (PLAYBOOK §15.3).
 */
export function useSubPickerGroups(weekEnding: string) {
  const [people, setPeople] = useState<SubPickerPerson[]>([]);
  const [worked, setWorked] = useState<SubPickerWorked[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const weekStart = (() => {
        const [y, m, d] = weekEnding.split('-').map(Number);
        const dt = new Date(y, m - 1, d - 6);
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
      })();

      const [wkRes, spRes, segRes] = await Promise.all([
        supabase.from('workers')
          .select('worker_id, first_name, preferred_name, last_name, employer_provider_id, worker_status')
          .neq('worker_status', 'terminated'),
        supabase.from('service_providers').select('provider_id, owner_name, partner_name'),
        supabase.from('shift_segments')
          .select('worker_id, facility_id')
          .gte('shift_date', weekStart).lte('shift_date', weekEnding),
      ]);
      if (cancelled) return;
      if (wkRes.error) { setError(errorMessage(wkRes.error, 'Could not load the people.')); return; }
      if (spRes.error) { setError(errorMessage(spRes.error, 'Could not load the subs.')); return; }
      if (segRes.error) { setError(errorMessage(segRes.error, 'Could not load this week’s hours.')); return; }
      setError(null);

      type W = {
        worker_id: string; first_name: string; preferred_name: string | null; last_name: string;
        employer_provider_id: string | null;
      };
      type P = { provider_id: string; owner_name: string | null; partner_name: string | null };
      const provName = new Map<string, string>();
      for (const p of (spRes.data ?? []) as P[]) {
        provName.set(p.provider_id, p.owner_name ?? p.partner_name ?? p.provider_id);
      }
      const ws = (wkRes.data ?? []) as W[];
      setPeople(ws.map((w) => ({
        worker_id: w.worker_id,
        name: `${w.preferred_name ?? w.first_name} ${w.last_name}`,
        provider_id: w.employer_provider_id,
        provider_name: w.employer_provider_id ? provName.get(w.employer_provider_id) ?? null : null,
      })).sort((a, b) => a.name.localeCompare(b.name)));

      // Who actually worked this week, and for whom. ⛔ Derived from the hours
      // and the roster, never from a hardcoded list or a date.
      const employerOf = new Map<string, string>();
      for (const w of ws) if (w.employer_provider_id) employerOf.set(w.worker_id, w.employer_provider_id);
      const byProv = new Map<string, { people: Set<string>; buildings: Set<string> }>();
      for (const s of (segRes.data ?? []) as Array<{ worker_id: string; facility_id: string | null }>) {
        const prov = employerOf.get(s.worker_id);
        if (!prov) continue;
        const bucket = byProv.get(prov) ?? { people: new Set(), buildings: new Set() };
        bucket.people.add(s.worker_id);
        if (s.facility_id) bucket.buildings.add(s.facility_id);
        byProv.set(prov, bucket);
      }
      setWorked(Array.from(byProv.entries())
        .map(([provider_id, b]) => ({
          provider_id,
          provider_name: provName.get(provider_id) ?? provider_id,
          people: b.people.size,
          buildings: b.buildings.size,
        }))
        .sort((a, b) => a.provider_name.localeCompare(b.provider_name)));
    })();
    return () => { cancelled = true; };
  }, [weekEnding]);

  return { people, worked, error };
}

export interface PayPeriodRow { period_id: string; period_start: string; period_end: string }

/** The real payroll calendar. ⛔ Read `error` and say which list failed — an
 *  empty period picker and a refused read look identical otherwise
 *  (PLAYBOOK §15.3). */
export function usePayPeriods() {
  const [rows, setRows] = useState<PayPeriodRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await supabase
        .from('pay_periods')
        .select('period_id, period_start, period_end')
        .eq('period_type', 'semimonthly_payroll')
        .order('period_start', { ascending: false })
        .limit(36);
      if (cancelled) return;
      if (err) {
        setError(errorMessage(err, 'Could not load the payroll calendar.'));
        setLoading(false);
        return;
      }
      setError(null);
      setRows((data ?? []) as unknown as PayPeriodRow[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return { rows, loading, error };
}

export function useExportHistory() {
  const [rows, setRows] = useState<ExportHistoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('period_exports')
        .select('export_id, export_type, period_id, provider_id, version, range_start, range_end, label, hours_total, amount_total, created_by_eid, created_at')
        .order('created_at', { ascending: false })
        .limit(50);
      if (err) throw err;
      setRows((data ?? []) as unknown as ExportHistoryRow[]);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the export history.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { rows, error, reload: load };
}

// ---------------------------------------------------------------------------
// The worker door (/clock) — one anon-callable RPC. Works with NO session:
// the supabase client falls back to the publishable key, which is the point.
// Identity refusals arrive as {ok:false} payloads, not thrown errors.
// ---------------------------------------------------------------------------
export async function workerClock(
  action: 'context' | 'punch' | 'hours' | 'resolve' | 'set_language' | 'where' | 'kiosk' | 'visit' | 'tonight' | 'photo_choice',
  phone: string,
  pin: string,
  payload: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc('rpc_worker_clock', {
    p_action: action,
    p_phone: phone,
    p_pin: pin,
    p_fingerprint: deviceFingerprint(),
    p_payload: payload,
  });
  if (error) throw error;
  return data as unknown as Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Devices — HR's verify-the-pairing surface (TCSD-0140). Reads under the
// app_read policy; verify/unbind are UPDATEs under the hr_update policy.
// ---------------------------------------------------------------------------
export function useDevices() {
  const [rows, setRows] = useState<import('../types').DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('punch_devices')
        .select('device_id, friendly_name, device_type, bound_facility_id, bound_worker_id, trust_tier, is_active, registered_by_eid, verified_at, first_seen_at, last_seen_at')
        .order('first_seen_at', { ascending: false })
        .limit(200);
      if (err) throw err;
      setRows((data ?? []) as unknown as import('../types').DeviceRow[]);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load devices.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, reload: load };
}

export async function verifyDevice(deviceId: string, verifierEid: string | null): Promise<void> {
  const { error } = await supabase
    .from('punch_devices')
    .update({ verified_at: new Date().toISOString(), trust_tier: 'hr_verified', registered_by_eid: verifierEid })
    .eq('device_id', deviceId);
  if (error) throw error;
}

export async function unbindDevice(deviceId: string): Promise<void> {
  const { error } = await supabase
    .from('punch_devices')
    .update({ bound_worker_id: null, verified_at: null, trust_tier: 'unregistered' })
    .eq('device_id', deviceId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Device fingerprint — a stable per-browser id (a browser cannot report its
// machine name; addendum B-4). Stored locally; named via rpc_time_register_device.
// ---------------------------------------------------------------------------
const FP_KEY = 'tcs-time-device-fp';

export function deviceFingerprint(): string | null {
  try {
    let fp = localStorage.getItem(FP_KEY);
    if (!fp) {
      fp = 'fp_' + crypto.randomUUID().replace(/-/g, '');
      localStorage.setItem(FP_KEY, fp);
    }
    return fp;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The kiosk iPad's device id (item "Punch kiosk iPad"). The same id and key as
// above, so setting the iPad up and punching on it name the same device — but
// held in TWO places and with the browser asked to keep them: localStorage,
// mirrored into IndexedDB, and navigator.storage.persist(). Either copy brings
// the other back. Measured 2026-10-22: the one person with many device rows is
// an Android phone whose id disappears about every two days — phones are not
// changed here. If the iPad still loses both, /clock/kiosk shows "not set up",
// so the loss is visible, never a silent fall-back to phone mode.
// ---------------------------------------------------------------------------
const FP_DB = 'tcs-time-device';
const FP_STORE = 'kv';

function idbOpen(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(FP_DB, 1);
      req.onupgradeneeded = () => { req.result.createObjectStore(FP_STORE); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await idbOpen();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const r = db.transaction(FP_STORE, 'readonly').objectStore(FP_STORE).get(key);
      r.onsuccess = () => resolve(typeof r.result === 'string' ? r.result : null);
      r.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  const db = await idbOpen();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(FP_STORE, 'readwrite');
      tx.objectStore(FP_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** The kiosk's device id, restored from whichever copy survived, then both kept. */
export async function kioskFingerprint(): Promise<string | null> {
  let local: string | null = null;
  try { local = localStorage.getItem(FP_KEY); } catch { /* blocked storage */ }
  const mirrored = await idbGet(FP_KEY);
  const fp = local ?? mirrored ?? deviceFingerprint();
  if (!fp) return null;
  try { if (!local) localStorage.setItem(FP_KEY, fp); } catch { /* blocked storage */ }
  if (mirrored !== fp) await idbSet(FP_KEY, fp);
  try { await navigator.storage?.persist?.(); } catch { /* not offered on this browser */ }
  return fp;
}

export interface KioskStatus {
  ok: boolean;
  kiosk: boolean;
  device_id?: string;
  facility_id?: string;
  place_display?: string;
  site_tz?: string;
}

/** Is THIS device a building's time clock? Reads only; never creates a device. */
export async function kioskStatus(): Promise<KioskStatus> {
  await kioskFingerprint();
  return (await workerClock('kiosk', '', '')) as unknown as KioskStatus;
}

const PUNCH_PHOTO_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/punch-photo`;

/**
 * Store the kiosk punch photo. The server checks the device is a kiosk and the
 * number + PIN match before it keeps anything. Returns the stored path, or null
 * when it could not be stored — the punch then goes through without a photo.
 */
export async function uploadPunchPhoto(phone: string, pin: string, photoBase64: string): Promise<string | null> {
  try {
    const res = await fetch(PUNCH_PHOTO_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ action: 'upload', phone, pin, fingerprint: deviceFingerprint(), photo_base64: photoBase64, mime: 'image/jpeg' }),
    });
    const data = (await res.json().catch(() => ({}))) as { photo_path?: string };
    return res.ok && data.photo_path ? data.photo_path : null;
  } catch {
    return null;
  }
}

/** A short-lived link to a punch photo, for staff who can already see the punch. */
export async function punchPhotoLink(photoUrl: string): Promise<string> {
  const jwt = getSupabaseToken();
  if (!jwt) throw new Error('You need to be signed in to open the photo.');
  const res = await fetch(PUNCH_PHOTO_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ action: 'view', photo_url: photoUrl }),
  });
  const data = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  if (!res.ok || !data.url) {
    // Ben, 1 Nov 2026: photos open for HR, payroll, executives and the building's metro AMs,
    // and are deleted after 90 days.
    if (data.error === 'removed') throw new Error('Removed after 90 days');
    if (data.error === 'not_allowed') throw new Error("HR and this metro's AMs only");
    throw new Error(data.error === 'punch_not_found' ? 'You cannot open this photo.' : 'The photo could not be opened.');
  }
  return data.url;
}

/** A building's kiosk photo switch (Ben, 1 Nov 2026): read it, or set it when `on` is given.
 *  Any full TCS employee may switch it — e.g. when the facility asks for PIN only. */
export interface KioskPhotosSetting { photos_on: boolean; off_since: string | null; kiosks: number }
export async function kioskPhotosSetting(facilityId: string, on?: boolean): Promise<KioskPhotosSetting> {
  const { data, error } = await supabase.rpc('rpc_time_kiosk_photos', {
    p_facility_id: facilityId,
    ...(on === undefined ? {} : { p_on: on }),
  });
  if (error) throw error;
  return data as unknown as KioskPhotosSetting;
}

export async function registerDevice(friendlyName: string, deviceType: string, boundFacilityId?: string | null): Promise<string> {
  const fp = deviceFingerprint();
  if (!fp) throw new Error('This browser does not allow device storage, so it cannot be named.');
  const { data, error } = await supabase.rpc('rpc_time_register_device', {
    p_fingerprint: fp,
    p_friendly_name: friendlyName,
    p_device_type: deviceType,
    p_bound_facility_id: boundFacilityId ?? null,
  });
  if (error) throw error;
  return data as string;
}

// ---------------------------------------------------------------------------
// In/Out Activity (item 885) — a READ. It writes no period_exports row and has
// no export type: the Payroll Summary keeps that job (prompt build rule 7).
//
// Read path: v_time_segments_detail (shifts, paid minutes, the building's
// clock, the place name) + punch_events (time, IP, capture mode, photo,
// superseding, reason) + workers / worker_role_codes. Every one of them is
// readable by hr, finance, executive and superuser — `smoke:inout-activity`
// proves it role by role. The rules live in ../inout-activity.ts.
// ---------------------------------------------------------------------------

/** PostgREST hands back at most 1000 rows a call; read every page. */
async function readAllPages<T>(
  build: (from: number, to: number) => PromiseLike<{ data: unknown; error: unknown }>,
): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let start = 0; ; start += PAGE) {
    const { data, error } = await build(start, start + PAGE - 1);
    if (error) throw error;
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

/** "2026-10-12" + 3 → "2026-10-15", on the calendar, never the viewer's clock. */
function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export interface InOutActivityData extends InOutInput {
  /** Active workers with their Buddy Punch username (the notice reads these). */
  activeWorkers: BuddyPunchWorker[];
}

export async function loadInOutActivity(from: string, to: string): Promise<InOutActivityData> {
  const segments = await readAllPages<InOutSegment>((a, b) => supabase
    .from('v_time_segments_detail')
    .select('segment_id, worker_id, facility_id, place_display, shift_date, started_at, ended_at, paid_minutes, segment_status, in_event_id, out_event_id, site_tz')
    .gte('shift_date', from)
    .lte('shift_date', to)
    .neq('segment_status', 'void')
    .order('segment_id')
    .range(a, b));

  // A shift's punches sit between the day before its date and a couple of
  // days after it (a night runs past midnight; an auto-close lands 12 h in).
  // Anything the window misses — a correction typed days later carries the
  // time it CORRECTS TO, so this is belt and braces — is fetched by id.
  const PUNCH_COLS = 'punch_event_id, event_type, event_at, capture_mode, ip_address, photo_url, supersedes_event_id, void_reason';
  const punches = await readAllPages<InOutPunch>((a, b) => supabase
    .from('punch_events')
    .select(PUNCH_COLS)
    .gte('event_at', `${addDaysYmd(from, -1)}T00:00:00Z`)
    .lt('event_at', `${addDaysYmd(to, 3)}T00:00:00Z`)
    .order('punch_event_id')
    .range(a, b));
  const have = new Set(punches.map((p) => p.punch_event_id));
  const missing = [...new Set(segments.flatMap((s) => [s.in_event_id, s.out_event_id]))]
    .filter((id): id is string => id != null && !have.has(id));
  for (let i = 0; i < missing.length; i += 150) {
    const { data, error } = await supabase.from('punch_events').select(PUNCH_COLS).in('punch_event_id', missing.slice(i, i + 150));
    if (error) throw error;
    punches.push(...((data ?? []) as InOutPunch[]));
  }

  // Only the auto-close exception matters to the report (prompt build rule 3).
  const exceptions = await readAllPages<InOutException>((a, b) => supabase
    .from('segment_exceptions')
    .select('segment_id, rule_code')
    .eq('rule_code', 'daily_ceiling_auto_close')
    .gte('created_at', `${addDaysYmd(from, -1)}T00:00:00Z`)
    .order('exception_id')
    .range(a, b));

  const [w, rc] = await Promise.all([
    readAllPages<{
      worker_id: string; employee_id: string | null; first_name: string | null; preferred_name: string | null;
      last_name: string | null; role_code: string | null; worker_status: string | null; buddy_punch_username: string | null;
      employer_provider_id: string | null;
    }>((a, b) => supabase
      .from('workers')
      .select('worker_id, employee_id, first_name, preferred_name, last_name, role_code, worker_status, buddy_punch_username, employer_provider_id')
      .order('worker_id')
      .range(a, b)),
    supabase.from('worker_role_codes').select('role_code, label'),
  ]);
  if (rc.error) throw rc.error;
  const roleLabel = new Map(((rc.data ?? []) as Array<{ role_code: string; label: string }>).map((r) => [r.role_code, r.label]));

  return {
    segments,
    punches: punches.map((p) => ({ ...p, ip_address: p.ip_address == null ? null : String(p.ip_address) })),
    exceptions,
    workers: w.map((x) => ({
      worker_id: x.worker_id,
      employee_id: x.employee_id,
      first_name: x.first_name,
      preferred_name: x.preferred_name,
      last_name: x.last_name,
      role_label: x.role_code ? (roleLabel.get(x.role_code) ?? x.role_code) : null,
      employer_provider_id: x.employer_provider_id,
    })),
    activeWorkers: w.filter((x) => x.worker_status === 'active'),
  };
}
